/**
 * <b2b-partner-map>: renders B2B partners on an OpenStreetMap map with Leaflet.
 * Leaflet is loaded from cdnjs only once the map scrolls near the viewport.
 */
(() => {
  if (customElements.get('b2b-partner-map')) return;

  const LEAFLET_VERSION = '1.9.4';
  const LEAFLET_BASE = `https://cdnjs.cloudflare.com/ajax/libs/leaflet/${LEAFLET_VERSION}`;
  let leafletPromise;

  function loadLeaflet() {
    if (window.L) return Promise.resolve(window.L);
    if (leafletPromise) return leafletPromise;

    leafletPromise = new Promise((resolve, reject) => {
      const css = document.createElement('link');
      css.rel = 'stylesheet';
      css.href = `${LEAFLET_BASE}/leaflet.min.css`;
      document.head.appendChild(css);

      const script = document.createElement('script');
      script.src = `${LEAFLET_BASE}/leaflet.min.js`;
      script.onload = () => resolve(window.L);
      script.onerror = reject;
      document.head.appendChild(script);
    });
    return leafletPromise;
  }

  function parseCoordinates(value) {
    if (!value) return null;
    // Accepts "51.0543, 3.7174" (Google Maps) as well as "51,0543 3,7174" (comma decimals).
    let numbers = String(value).match(/-?\d+[.,]\d+/g);
    if (!numbers || numbers.length < 2) numbers = String(value).match(/-?\d+(?:\.\d+)?/g);
    if (!numbers || numbers.length < 2) return null;
    const lat = parseFloat(numbers[0].replace(',', '.'));
    const lng = parseFloat(numbers[1].replace(',', '.'));
    if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
    return [lat, lng];
  }

  function popupContent(partner) {
    const popup = document.createElement('div');
    popup.className = 'b2b-map__popup';

    if (partner.logo) {
      const logo = document.createElement('img');
      logo.className = 'b2b-map__popup-logo';
      logo.src = partner.logo;
      logo.alt = '';
      logo.width = 48;
      logo.height = 48;
      popup.appendChild(logo);
    }

    const wrapper = document.createElement('div');
    popup.appendChild(wrapper);

    const name = document.createElement('span');
    name.className = 'b2b-map__popup-name';
    name.textContent = partner.name;
    wrapper.appendChild(name);

    const meta = [partner.type, [partner.city, partner.country].filter(Boolean).join(', ')]
      .filter(Boolean)
      .join(' · ');
    if (meta) {
      const metaEl = document.createElement('span');
      metaEl.className = 'b2b-map__popup-meta';
      metaEl.textContent = meta;
      wrapper.appendChild(metaEl);
    }

    if (partner.url) {
      const link = document.createElement('a');
      link.className = 'b2b-map__popup-link';
      link.href = partner.url;
      link.target = '_blank';
      link.rel = 'noopener';
      link.textContent = 'Visit website';
      wrapper.appendChild(link);
    }
    return popup;
  }

  class B2BPartnerMap extends HTMLElement {
    connectedCallback() {
      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          loadLeaflet()
            .then((L) => this.render(L))
            .catch(() => this.classList.add('b2b-map__canvas--failed'));
        },
        { rootMargin: '300px' }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.map?.remove();
      this.map = null;
    }

    render(L) {
      if (this.map) return;

      let partners = [];
      try {
        partners = JSON.parse(this.querySelector('[data-partners]').textContent);
      } catch (error) {
        partners = [];
      }

      const center = parseCoordinates(this.dataset.center) || [50.5, 8];
      const zoom = parseInt(this.dataset.zoom, 10) || 4;

      this.map = L.map(this.querySelector('.b2b-map__map'), {
        center,
        zoom,
        scrollWheelZoom: false,
        dragging: !L.Browser.mobile,
        tap: false,
        attributionControl: true,
      });

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 18,
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>',
      }).addTo(this.map);

      // Enable scroll-zoom only after the visitor interacts with the map.
      this.map.once('focus click', () => this.map.scrollWheelZoom.enable());

      const icon = L.divIcon({
        className: '',
        html: '<span class="b2b-map__marker"></span>',
        iconSize: [28, 28],
        iconAnchor: [14, 14],
        popupAnchor: [0, -14],
      });

      const points = [];
      partners.forEach((partner) => {
        const position = parseCoordinates(partner.coordinates);
        if (!position) return;
        points.push(position);
        L.marker(position, { icon, title: partner.name, keyboard: true })
          .bindPopup(popupContent(partner))
          .addTo(this.map);
      });

      // Frame all partners. The map can be created before its container has its final
      // size (it loads as it scrolls into view), so frame again once layout has settled.
      const frame = () => {
        if (!this.map) return;
        this.map.invalidateSize();
        if (points.length > 1) {
          this.map.fitBounds(points, { padding: [48, 48], maxZoom: 9 });
        } else if (points.length === 1) {
          this.map.setView(points[0], 8);
        }
      };
      frame();
      requestAnimationFrame(frame);
      setTimeout(frame, 400);
    }
  }

  customElements.define('b2b-partner-map', B2BPartnerMap);
})();
